import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import CustomerLayout from '../../components/layouts/CustomerLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Plus, 
  Clock, 
  Truck, 
  Package,
  ArrowRight,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [waybills, setWaybills] = useState({ awaiting_quote: [], awaiting_acceptance: [], in_progress: [], delivered: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, waybillsRes] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard/customer-stats`),
          axios.get(`${API_URL}/api/waybills?limit=50`)
        ]);

        setStats(statsRes.data);

        // Categorize waybills
        const wbs = waybillsRes.data.waybills;
        setWaybills({
          awaiting_quote: wbs.filter(w => ['quote_requested', 'in_costing'].includes(w.status)),
          awaiting_acceptance: wbs.filter(w => w.status === 'quote_ready'),
          in_progress: wbs.filter(w => ['confirmed', 'acknowledged', 'accepted', 'dispatch_scheduled', 'collected', 'in_transit', 'at_customs', 'out_for_delivery'].includes(w.status)),
          delivered: wbs.filter(w => w.status === 'delivered').slice(0, 5)
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `R ${Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="space-y-8 animate-fade-in" data-testid="customer-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Shipment Overview</h1>
            <p className="text-slate-600 mt-1">Manage your logistical pipeline and pending quotes.</p>
          </div>
          <Link to="/create-waybill">
            <Button className="btn-primary" data-testid="new-shipment-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Shipment
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="card-elevated" data-testid="stat-active">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Active Shipments</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_active || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center">
                  <Package className="h-6 w-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-quotes">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Quotes Pending</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.awaiting_quote || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-acceptance">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Awaiting Acceptance</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.awaiting_acceptance || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Awaiting Quote Section */}
        <Card className="card-elevated" data-testid="awaiting-quote-section">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <CardTitle className="section-title">Awaiting Quote</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {waybills.awaiting_quote.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Waybill ID</th>
                      <th>Submission Date</th>
                      <th>Origin / Destination</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waybills.awaiting_quote.map((wb) => (
                      <tr key={wb.waybill_id} onClick={() => navigate(`/waybill/${wb.waybill_id}`, { state: { from: '/dashboard' } })} className="cursor-pointer hover:bg-slate-50 transition-colors" data-testid={`waybill-row-${wb.reference_number}`}>
                        <td>
                          <span className="text-teal-700 font-medium">
                            {wb.reference_number}
                          </span>
                        </td>
                        <td>{formatDate(wb.submission_date)}</td>
                        <td>
                          <span className="text-slate-900">{wb.origin_city}</span>
                          <span className="text-slate-400 mx-2">→</span>
                          <span className="text-slate-900">{wb.destination_city}</span>
                        </td>
                        <td><StatusBadge status={wb.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                No waybills awaiting quote
              </div>
            )}
          </CardContent>
        </Card>

        {/* Awaiting Acceptance Section */}
        <Card className="card-elevated" data-testid="awaiting-acceptance-section">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <CardTitle className="section-title">Awaiting Acceptance</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {waybills.awaiting_acceptance.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {waybills.awaiting_acceptance.map((wb) => (
                  <div 
                    key={wb.waybill_id} 
                    className="bg-white border border-slate-100 rounded-xl p-5 hover:shadow-md transition-shadow"
                    data-testid={`acceptance-card-${wb.reference_number}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-xs text-teal-600 font-semibold uppercase tracking-wide">New Quote Ready</p>
                        <p className="text-lg font-bold text-slate-900 mt-1">{wb.reference_number}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Quoted Amount</p>
                        <p className="text-xl font-bold text-teal-700">{formatCurrency(wb.quoted_amount)}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">
                      {wb.origin_city} — {wb.destination_city}
                    </p>
                    <div className="flex space-x-2">
                      <Link to={`/waybill/${wb.waybill_id}`} state={{ from: '/dashboard' }} className="flex-1">
                        <Button className="w-full btn-primary text-sm" data-testid={`accept-btn-${wb.reference_number}`}>
                          Review & Accept
                        </Button>
                      </Link>
                      <Link to={`/waybill/${wb.waybill_id}`} state={{ from: '/dashboard' }}>
                        <Button variant="outline" className="btn-outline text-sm" data-testid={`decline-btn-${wb.reference_number}`}>
                          Decline
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-slate-500 py-8">
                No quotes awaiting your acceptance
              </div>
            )}
          </CardContent>
        </Card>

        {/* In Progress Section */}
        <Card className="card-elevated" data-testid="in-progress-section">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Truck className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle className="section-title">In Progress</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {waybills.in_progress.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Waybill ID</th>
                      <th>Route</th>
                      <th>Status</th>
                      <th>ETA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waybills.in_progress.map((wb) => (
                      <tr 
                        key={wb.waybill_id}
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => navigate(`/waybill/${wb.waybill_id}`, { state: { from: '/dashboard' } })}
                      >
                        <td>
                          <span className="text-teal-700 font-medium">{wb.reference_number}</span>
                        </td>
                        <td>
                          <span>{wb.origin_city}</span>
                          <span className="text-slate-400 mx-2">→</span>
                          <span>{wb.destination_city}</span>
                        </td>
                        <td><StatusBadge status={wb.status} /></td>
                        <td>{formatDate(wb.estimated_delivery_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                No shipments in progress
              </div>
            )}
          </CardContent>
        </Card>

        {/* End of dashboard sections */}
      </div>
    </CustomerLayout>
  );
};

export default CustomerDashboard;
